import { Component, Input, OnChanges, SimpleChanges, OnInit } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { CommonModule } from '@angular/common';
import { MatchData } from '../../../interfaces/matchData';
import { MatButtonModule } from '@angular/material/button';
import { ParticipantBoxComponent } from '../../participant/participant-box/participant-box.component';
import { Participant } from '../../../interfaces/participant';
import { HasPermissionDirective } from '../../../directives/has-permission.directive';
import { Permission, User } from '../../../interfaces/permission';
import { CrossmatchService } from '../../../services/crossmatch.service';
import { Crossmatch } from '../../../interfaces/crossmatch';
import { AddCrossmatchComponent } from '../../crossmatch/add-crossmatch/add-crossmatch.component';
import { AddExcludedComponent } from '../../combinations/add-excluded/add-excluded.component';
import { NotifyService } from '../../../common/notify/notify.service';
import { AuthService } from '../../../auth/auth.service';
import { ChainStatusService } from '../../../services/chain-status.service';
import { ChainStatus } from '../../../interfaces/chainStatus';
import { ChainLockDialogComponent, ChainLockDialogResult } from '../chain-lock-dialog/chain-lock-dialog.component';
import { ChainCommentDialogComponent, ChainCommentDialogResult } from '../chain-comment-dialog/chain-comment-dialog.component';
import { SelectTransplantDialogComponent, SelectTransplantDialogResult } from '../select-transplant-dialog/select-transplant-dialog.component';
import { UnselectTransplantDialogComponent, UnselectTransplantDialogResult } from '../unselect-transplant-dialog/unselect-transplant-dialog.component';
import { ConfirmTransplantationDialogComponent, ConfirmTransplantationDialogResult } from '../confirm-transplantation-dialog/confirm-transplantation-dialog.component';
import { ParticipantService } from '../../../services/participant.service';
import { forkJoin } from 'rxjs';
import { AnonymizationService, AnonymizedParticipant, AnonymizationContext } from '../../../services/anonymization.service';
import { Donor } from '../../../interfaces/donor';
import { Recipient } from '../../../interfaces/recipient';
import { PairService } from '../../../services/pair.service';
import { Pair } from '../../../interfaces/pair';
import { PairComparisonDialogComponent } from '../pair-comparison-dialog/pair-comparison-dialog.component';

@Component({
  selector: 'app-match-chain',
  imports: [CommonModule, MatCardModule, MatListModule, MatIconModule, MatChipsModule, MatButtonModule, MatTooltipModule, MatMenuModule, MatDividerModule, HasPermissionDirective],
  templateUrl: './match-chain.component.html',
  styleUrl: './match-chain.component.scss'
})
export class MatchChainComponent implements OnInit, OnChanges {
  @Input() data: MatchData[] = []; // Input to accept match data for the chain
  @Input() chainNumber!: string; // Input to accept the chain number
  @Input() matchRunNumber!: string;
  @Input() matchRunId!: string; // Match run ID for API calls
  @Input() allParticipants!: Participant[]; // Input to assign all participants
  @Input() currentMatchRunDate!: number; // Current matchrun date created timestamp
  @Input() nextMatchRunDate?: number; // Next matchrun date created timestamp (if exists)
  @Input() isHistoricalMatchRun: boolean = false; // Whether this is a historical (read-only) matchrun
  // OPTIMIZATION: Accept pre-loaded crossmatches and chain status from parent to avoid N API calls
  @Input() crossmatches?: Crossmatch[]; // Pre-loaded crossmatches for this chain
  @Input() chainStatus?: ChainStatus | null; // Pre-loaded chain status for this chain
  alternativeRecipients: Participant[] = [];
  isNewChain: boolean = false; // Always false - chains appearing in new matchruns aren't meaningful changes
  isChainStatusNew: boolean = false; // Track if chain lock status is new in this matchrun
  currentUser: User | null = null; // Store current user info
  isChainCommentExpanded: boolean = true; // Track if chain comment is expanded (default to expanded)

  // Cache grouped data to prevent recalculation on hover
  private _groupedData: { recipient: Participant | null; donor: Participant | null; alternative: boolean }[] = [];

  // Cache anonymized grouped data
  private _anonymizedGroupedData: { recipient: AnonymizedParticipant | null; donor: AnonymizedParticipant | null; alternative: boolean }[] = [];

  // Anonymization context
  private _anonymizationContext: AnonymizationContext | null = null;

  // OPTIMIZATION: Participant map for O(1) lookups instead of O(N) array.find()
  private participantMap: Map<string, Participant> = new Map();

  // Expose Permission enum to template
  Permission = Permission;

  // Track if chain has specific crossmatch types
  hasPositiveCrossmatch = false;
  hasNegativeCrossmatch = false;
  chainHasExcludedCombination = false;

  // Track confirmed pairs for transplantation
  confirmedPairs: Set<string> = new Set();

  constructor(
    private readonly dialog: MatDialog,
    private readonly crossmatchService: CrossmatchService,
    private readonly notifyService: NotifyService,
    public readonly authService: AuthService,
    private readonly chainStatusService: ChainStatusService,
    private readonly participantService: ParticipantService,
    private readonly anonymizationService: AnonymizationService,
    private readonly pairService: PairService
  ) {}

  ngOnInit(): void {
    // OPTIMIZATION: Build participant map for O(1) lookups
    this.buildParticipantMap();

    // OPTIMIZATION: Only load crossmatches if not provided via @Input
    if (this.crossmatches === undefined) {
      this.loadCrossmatches();
    } else {
      // Crossmatches provided, just update flags
      this.updateChainCrossmatchFlags();
    }

    // OPTIMIZATION: Only load chain status if not provided via @Input
    if (this.chainStatus === undefined) {
      this.loadChainStatus();
    } else {
      // Chain status provided, just check if it's new
      this.checkIfChainStatusNew();
    }

    this.updateGroupedData();
    // Removed checkIfNewChain() - chains appearing in new matchruns aren't changes
    this.loadCurrentUser();
  }

  private loadCurrentUser(): void {
    this.authService.getUser().subscribe({
      next: (user) => {
        this.currentUser = user;
        // Re-apply anonymization now that user is loaded
        this.applyAnonymization();
      },
      error: (error) => {
        console.error('Error loading current user:', error);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['allParticipants'] && this.allParticipants) {
      // OPTIMIZATION: Rebuild participant map when allParticipants change
      this.buildParticipantMap();
      this.updateGroupedData();
    }
    if (changes['data'] && this.data) {
      this.updateGroupedData();
    }
  }

  // Group recipients and donors together (anonymized if needed)
  get groupedData(): { recipient: AnonymizedParticipant | null; donor: AnonymizedParticipant | null; alternative: boolean }[] {
    return this._anonymizedGroupedData;
  }

  private updateGroupedData(): void {
    const grouped: { recipient: Participant | null; donor: Participant | null; alternative: boolean }[] = [];

    // Sort data by orderIndex to ensure correct pairing order
    const sortedData = [...this.data].sort((a, b) => (a.orderIndex || 1) - (b.orderIndex || 1));

    // Separate recipients and donors while maintaining order
    const recipients = sortedData.filter(item => item.type === 'P');
    const donors = sortedData.filter(item => item.type === 'D');

    // Pair recipients with donors based on their position in Excel (1→2, 3→4, etc.)
    // Each recipient should pair with the next donor in sequence
    let donorIndex = 0;
    let latestMatchedDonor: MatchData | undefined;

    recipients.forEach((recipient) => {
      if (recipient.alternative) {
        // Handle alternative recipients separately
        return;
      }

      // Find the next available donor
      const donor = donors[donorIndex];
      if (donor) {
        latestMatchedDonor = donor;
        grouped.push({
          recipient: this.findParticipant(recipient.enisNumber),
          donor: this.findParticipant(donor.enisNumber),
          alternative: false
        });
        donorIndex++;
      } else {
        // No donor available, add recipient without pair
        grouped.push({
          recipient: this.findParticipant(recipient.enisNumber),
          donor: null,
          alternative: false
        });
      }
    });

    // Handle any remaining unmatched donors
    while (donorIndex < donors.length) {
      const donor = donors[donorIndex];
      grouped.push({
        recipient: null,
        donor: this.findParticipant(donor.enisNumber),
        alternative: false
      });
      donorIndex++;
    }

    // Handle recipients with alternative = true
    const alternativeRecipient = recipients.find((recipient) => recipient.alternative);
    if (alternativeRecipient && latestMatchedDonor) {
      grouped.push({
        recipient: this.findParticipant(alternativeRecipient.enisNumber),
        donor: this.findParticipant(latestMatchedDonor.enisNumber),
        alternative: true
      });
    }

    this._groupedData = grouped;

    // Apply anonymization
    this.applyAnonymization();
  }

  /**
   * Apply anonymization to grouped data based on user role and center
   */
  private applyAnonymization(): void {
    if (!this.currentUser) {
      // If user is not loaded yet, use raw data
      this._anonymizedGroupedData = this._groupedData.map(pair => ({
        recipient: pair.recipient as AnonymizedParticipant | null,
        donor: pair.donor as AnonymizedParticipant | null,
        alternative: pair.alternative
      }));
      return;
    }

    // Create anonymization context
    this._anonymizationContext = this.anonymizationService.createAnonymizationContext(
      this.currentUser.activeRole.enumName,
      this.currentUser.centerName,
      this._groupedData
    );

    // Apply anonymization to each pair
    this._anonymizedGroupedData = this._groupedData.map((pair, index) => ({
      recipient: this.anonymizationService.anonymizeParticipant(
        pair.recipient,
        'recipient',
        index,
        this._anonymizationContext!
      ),
      donor: this.anonymizationService.anonymizeParticipant(
        pair.donor,
        'donor',
        index,
        this._anonymizationContext!
      ),
      alternative: pair.alternative
    }));
  }

  /**
   * OPTIMIZATION: Build participant map for O(1) lookups
   * Replaces O(N) array.find() operations with O(1) map.get()
   * For 200 participants × 20 lookups per chain = 4,000 operations per chain
   * With 10 chains = 40,000 array iterations reduced to 200 map insertions + 200 map lookups
   */
  private buildParticipantMap(): void {
    this.participantMap.clear();
    if (!this.allParticipants) {
      return;
    }
    this.allParticipants.forEach(participant => {
      this.participantMap.set(participant.enisNumber, participant);
    });
  }

  /**
   * Helper method to find a participant by enisNumber
   * OPTIMIZATION: Uses Map.get() for O(1) lookup instead of Array.find() for O(N) lookup
   * Returns null if not found - we'll handle nulls properly in the anonymization logic
   */
  private findParticipant(enisNumber: string | null): Participant | null {
    if (!enisNumber) {
      return null;
    }
    return this.participantMap.get(enisNumber) || null;
  }
  isAlternativeRecipient(participant: Participant | null): boolean {
    if (!participant) {
      return false;
    }
    return this.alternativeRecipients.some((altRecipient) => altRecipient.enisNumber === participant.enisNumber);
  }
  openParticipantDialog(participant: AnonymizedParticipant | null, type: 'recipient' | 'donor'): void {
    if (!participant || !participant.enisNumber) {
      return;
    }

    // Don't open dialog for anonymized participants
    // Check if ENIS is masked (completely anonymized) OR if medical data is masked (partially anonymized)
    if (participant.enisNumber === '******' || participant['abo'] === '******') {
      this.notifyService.withAutodismiss('You do not have permission to view details for this participant');
      return;
    }

    // OPTIMIZATION: Use participantMap for O(1) lookup instead of array.find()
    const arrayParticipant = this.participantMap.get(participant.enisNumber);

    if (!arrayParticipant) {
      console.warn('Participant not found in participant map');
      return;
    }

    if (type === 'recipient') {
      this.dialog.open(ParticipantBoxComponent, {
        width: '400px',
        data: {
          recipient: arrayParticipant
        }
      });
    } else {
      this.dialog.open(ParticipantBoxComponent, {
        width: '400px',
        data: {
          donor: arrayParticipant
        }
      });
    }
  }

  openPairComparisonDialog(pair: { recipient: AnonymizedParticipant | null; donor: AnonymizedParticipant | null; alternative: boolean }): void {
    if (!pair.recipient || !pair.donor) return;

    if (pair.recipient.enisNumber === '******' || pair.recipient['abo'] === '******' ||
        pair.donor.enisNumber === '******' || pair.donor['abo'] === '******') {
      this.notifyService.withAutodismiss('You do not have permission to view details for this participant');
      return;
    }

    const recipient = this.participantMap.get(pair.recipient.enisNumber) as Recipient;
    const donor = this.participantMap.get(pair.donor.enisNumber) as Donor;

    if (!recipient || !donor) return;

    this.dialog.open(PairComparisonDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      data: { recipient, donor }
    });
  }

  private loadCrossmatches(): void {
    // PHASE 3 OPTIMIZATION: Crossmatches are now pre-filtered by matchrun period on the backend
    // No need to fetch all crossmatches and filter client-side
    this.crossmatchService.getAllCrossmatches().subscribe({
      next: (crossmatches) => {
        this.crossmatches = crossmatches;
        this.updateChainCrossmatchFlags();
      },
      error: (error) => {
        console.error('Error loading crossmatches:', error);
      }
    });
  }

  private updateChainCrossmatchFlags(): void {
    this.hasPositiveCrossmatch = false;
    this.hasNegativeCrossmatch = false;
    this.chainHasExcludedCombination = false;

    for (const pair of this._groupedData) {
      // Check for crossmatch results
      const crossmatchResult = this.findCrossmatchResultForPair(pair);
      if (crossmatchResult) {
        if (crossmatchResult.positive === true) {
          this.hasPositiveCrossmatch = true;
        } else if (crossmatchResult.positive === false) {
          this.hasNegativeCrossmatch = true;
        }
      }

      // Check for excluded combinations
      const excludedCombination = this.findExcludedCombinationForPair(pair);
      if (excludedCombination) {
        this.chainHasExcludedCombination = true;
      }
    }
  }

  getCrossmatchStatus(pair: { recipient: AnonymizedParticipant | Participant | null; donor: AnonymizedParticipant | Participant | null; alternative: boolean }): string {
    const crossmatch = this.findCrossmatchResultForPair(pair);
    if (!crossmatch) {
      return '';
    }

    if (crossmatch.positive === true) {
      return 'Positive';
    } else if (crossmatch.positive === false) {
      return 'Negative';
    }

    return 'Pending';
  }

  getCrossmatchResult(pair: { recipient: AnonymizedParticipant | Participant | null; donor: AnonymizedParticipant | Participant | null; alternative: boolean }): string {
    const crossmatch = this.findCrossmatchResultForPair(pair);
    if (!crossmatch) {
      return '';
    }

    if (crossmatch.positive === true) {
      return 'Positive';
    } else if (crossmatch.positive === false) {
      return 'Negative';
    }

    return 'Pending result';
  }

  // Get only the crossmatch result label (positive/negative/pending), ignoring excluded status
  getCrossmatchResultLabel(pair: { recipient: AnonymizedParticipant | Participant | null; donor: AnonymizedParticipant | Participant | null; alternative: boolean }): string {
    const crossmatch = this.findCrossmatchResultForPair(pair);
    if (!crossmatch) {
      return '';
    }

    if (crossmatch.positive === true) {
      return 'Positive';
    } else if (crossmatch.positive === false) {
      return 'Negative';
    }

    return 'Pending result';
  }

  hasCrossmatch(pair: { recipient: AnonymizedParticipant | Participant | null; donor: AnonymizedParticipant | Participant | null; alternative: boolean }): boolean {
    return this.hasCrossmatchResult(pair) || this.hasExcludedCombination(pair);
  }

  // Find the latest crossmatch RESULT (positive/negative) for a pair
  private findCrossmatchResultForPair(pair: { recipient: AnonymizedParticipant | Participant | null; donor: AnonymizedParticipant | Participant | null; alternative: boolean }): Crossmatch | null {
    if (!pair.recipient || !pair.donor) {
      return null;
    }

    // Anonymized participants (masked ENIS) won't match any crossmatches
    if (pair.recipient.enisNumber === '******' || pair.donor.enisNumber === '******') {
      return null;
    }

    // Check if crossmatches are loaded
    if (!this.crossmatches) {
      return null;
    }

    // Find ALL crossmatches with a result (positive field is defined) for this pair
    const matchingCrossmatches = this.crossmatches.filter(crossmatch =>
      crossmatch.recipientEnis === pair.recipient!.enisNumber &&
      crossmatch.donorEnis === pair.donor!.enisNumber &&
      (crossmatch.positive !== undefined && crossmatch.positive !== null)
    );

    if (matchingCrossmatches.length === 0) {
      return null;
    }

    // Sort by dateCreated descending and return the most recent
    return matchingCrossmatches.sort((a, b) => 
      new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime()
    )[0];
  }

  // Find the latest EXCLUDED COMBINATION for a pair
  private findExcludedCombinationForPair(pair: { recipient: AnonymizedParticipant | Participant | null; donor: AnonymizedParticipant | Participant | null; alternative: boolean }): Crossmatch | null {
    if (!pair.recipient || !pair.donor) {
      return null;
    }

    // Anonymized participants (masked ENIS) won't match any crossmatches
    if (pair.recipient.enisNumber === '******' || pair.donor.enisNumber === '******') {
      return null;
    }

    // Check if crossmatches are loaded
    if (!this.crossmatches) {
      return null;
    }

    // Find ALL excluded combinations for this pair
    const matchingCrossmatches = this.crossmatches.filter(crossmatch =>
      crossmatch.recipientEnis === pair.recipient!.enisNumber &&
      crossmatch.donorEnis === pair.donor!.enisNumber &&
      crossmatch.excluded === true
    );

    if (matchingCrossmatches.length === 0) {
      return null;
    }

    // Sort by dateCreated descending and return the most recent
    return matchingCrossmatches.sort((a, b) => 
      new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime()
    )[0];
  }

  addCrossmatch(pair: { recipient: AnonymizedParticipant | null; donor: AnonymizedParticipant | null; alternative: boolean }, type?: 'Positive' | 'Negative'): void {
    if (!pair.recipient || !pair.donor) {
      this.notifyService.withAutodismiss('Both recipient and donor must be available to add crossmatch');
      return;
    }

    // Check if participants are anonymized (fully or partially)
    if (pair.recipient.enisNumber === '******' || pair.donor.enisNumber === '******' ||
        pair.recipient['abo'] === '******' || pair.donor['abo'] === '******') {
      this.notifyService.withAutodismiss('You do not have permission to add crossmatches for anonymized participants');
      return;
    }

    // OPTIMIZATION: Look up actual participants from participantMap (O(1) instead of O(N))
    const recipient = this.participantMap.get(pair.recipient!.enisNumber);
    const donor = this.participantMap.get(pair.donor!.enisNumber);

    if (!recipient || !donor) {
      this.notifyService.withAutodismiss('Could not find participant data');
      return;
    }

    const dialogRef = this.dialog.open(AddCrossmatchComponent, {
      width: '500px',
      data: {
        preselectedRecipient: recipient,
        preselectedDonor: donor,
        matchChainContext: true,
        type: type // Pass the selected type
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadCrossmatches();
        this.notifyService.withAutodismiss('Crossmatch added successfully');
      }
    });
  }

  addExcludedCombination(pair: { recipient: AnonymizedParticipant | null; donor: AnonymizedParticipant | null; alternative: boolean }): void {
    if (!pair.recipient || !pair.donor) {
      this.notifyService.withAutodismiss('Both recipient and donor must be available to add excluded combination');
      return;
    }

    // Check if participants are anonymized (fully or partially)
    if (pair.recipient.enisNumber === '******' || pair.donor.enisNumber === '******' ||
        pair.recipient['abo'] === '******' || pair.donor['abo'] === '******') {
      this.notifyService.withAutodismiss('You do not have permission to add excluded combinations for anonymized participants');
      return;
    }

    // OPTIMIZATION: Look up actual participants from participantMap (O(1) instead of O(N))
    const recipient = this.participantMap.get(pair.recipient!.enisNumber);
    const donor = this.participantMap.get(pair.donor!.enisNumber);

    if (!recipient || !donor) {
      this.notifyService.withAutodismiss('Could not find participant data');
      return;
    }

    const dialogRef = this.dialog.open(AddExcludedComponent, {
      width: '500px',
      data: {
        preselectedRecipient: recipient,
        preselectedDonor: donor
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadCrossmatches();
        this.notifyService.withAutodismiss('Excluded combination added successfully');
      }
    });
  }

  deleteCrossmatch(pair: { recipient: AnonymizedParticipant | null; donor: AnonymizedParticipant | null; alternative: boolean }): void {
    if (!pair.recipient || !pair.donor) {
      this.notifyService.withAutodismiss('Both recipient and donor must be available to delete crossmatch');
      return;
    }

    const crossmatch = this.findCrossmatchResultForPair(pair);
    if (!crossmatch) {
      this.notifyService.withAutodismiss('No crossmatch result found for this pair');
      return;
    }

    if (confirm('Are you sure you want to delete this crossmatch result?')) {
      // Simply delete the crossmatch result entity
      // (We're now tracking crossmatch results and excluded combinations separately)
      this.crossmatchService.deleteCrossmatch(crossmatch.id).subscribe({
        next: () => {
          this.loadCrossmatches();
          this.notifyService.withAutodismiss('Crossmatch result deleted successfully');
        },
        error: (error) => {
          console.error('Error deleting crossmatch result:', error);
          this.notifyService.withAutodismiss('Failed to delete crossmatch result. Please try again.');
        }
      });
    }
  }

  private loadChainStatus(): void {
    if (!this.matchRunId || !this.chainNumber) {
      return;
    }

    this.chainStatusService.getChainStatus(this.matchRunId, parseInt(this.chainNumber)).subscribe({
      next: (status) => {
        this.chainStatus = status;
        this.checkIfChainStatusNew();
      },
      error: (error) => {
        // It's okay if no status exists yet - this means the chain is unlocked
        this.chainStatus = null;
        this.isChainStatusNew = false;
      }
    });
  }

  isChainLocked(): boolean {
    // Chain is only locked if it has a chainStatus with a non-null status
    // Chains with null status are comment-only and not locked
    return this.chainStatus != null && this.chainStatus.status != null;
  }

  toggleChainLock(): void {
    const action = this.isChainLocked() ? 'unlock' : 'lock';

    const dialogRef = this.dialog.open(ChainLockDialogComponent, {
      width: '600px',
      data: {
        chainNumber: this.chainNumber,
        matchRunNumber: this.matchRunNumber,
        action: action
      }
    });

    dialogRef.afterClosed().subscribe((result: ChainLockDialogResult) => {
      if (result?.confirmed) {
        if (action === 'lock') {
          this.lockChain(result.comment);
        } else {
          this.unlockChain();
        }
      }
    });
  }

  private lockChain(comment?: string): void {
    // Always use 'provisional' as the default status when locking a chain
    this.chainStatusService.setChainStatus(this.matchRunId, parseInt(this.chainNumber), 'provisional', comment).subscribe({
      next: (status) => {
        this.chainStatus = status;
        this.checkIfChainStatusNew(); // Recheck if status is new after locking
        this.notifyService.withAutodismiss('Chain locked successfully');
      },
      error: (error) => {
        console.error('Error locking chain:', error);
        this.notifyService.withAutodismiss('Failed to lock chain. Please try again.');
      }
    });
  }

  private unlockChain(): void {
    this.chainStatusService.deleteChainStatus(this.matchRunId, parseInt(this.chainNumber)).subscribe({
      next: () => {
        this.chainStatus = null;
        this.isChainStatusNew = false; // Reset flag after unlocking
        this.notifyService.withAutodismiss('Chain unlocked successfully');
      },
      error: (error) => {
        console.error('Error unlocking chain:', error);
        this.notifyService.withAutodismiss('Failed to unlock chain. Please try again.');
      }
    });
  }

  // Removed checkIfNewChain() method - chains appearing in new matchruns aren't meaningful changes
  // Orange bubbles should only indicate user actions (locks, crossmatches), not algorithm results

  private checkIfChainStatusNew(): void {
    // Chain status is new if it was set between current and next matchrun
    if (!this.chainStatus || !this.currentMatchRunDate) {
      this.isChainStatusNew = false;
      return;
    }

    const statusDate = this.chainStatus.dateSet;
    const currentRunDate = this.currentMatchRunDate * 1000; // Convert from seconds to milliseconds

    // If there's a next matchrun, check if status was set between current and next
    if (this.nextMatchRunDate) {
      const nextRunDate = this.nextMatchRunDate * 1000;
      this.isChainStatusNew = statusDate >= currentRunDate && statusDate < nextRunDate;
    } else {
      // If this is the latest matchrun, check if status was set after this run started
      this.isChainStatusNew = statusDate >= currentRunDate;
    }
  }

  isCrossmatchNew(crossmatch: Crossmatch): boolean {
    // Crossmatch is new if it was created between current and next matchrun
    if (!crossmatch || !this.currentMatchRunDate) {
      return false;
    }

    const crossmatchDate = new Date(crossmatch.dateCreated).getTime();
    const currentRunDate = this.currentMatchRunDate * 1000; // Convert from seconds to milliseconds

    // If there's a next matchrun, check if crossmatch was created between current and next
    if (this.nextMatchRunDate) {
      const nextRunDate = this.nextMatchRunDate * 1000;
      return crossmatchDate >= currentRunDate && crossmatchDate < nextRunDate;
    }

    // If this is the latest matchrun, check if crossmatch was created after this run started
    return crossmatchDate >= currentRunDate;
  }

  isPairCrossmatchNew(pair: { recipient: AnonymizedParticipant | Participant | null; donor: AnonymizedParticipant | Participant | null; alternative: boolean }): boolean {
    // Check if either the crossmatch result or excluded combination is new
    const crossmatchResult = this.findCrossmatchResultForPair(pair);
    const excludedCombination = this.findExcludedCombinationForPair(pair);
    
    const resultIsNew = crossmatchResult ? this.isCrossmatchNew(crossmatchResult) : false;
    const excludedIsNew = excludedCombination ? this.isCrossmatchNew(excludedCombination) : false;
    
    return resultIsNew || excludedIsNew;
  }

  // Check if the crossmatch result for a pair is new
  isPairCrossmatchResultNew(pair: { recipient: AnonymizedParticipant | Participant | null; donor: AnonymizedParticipant | Participant | null; alternative: boolean }): boolean {
    const crossmatchResult = this.findCrossmatchResultForPair(pair);
    return crossmatchResult ? this.isCrossmatchNew(crossmatchResult) : false;
  }

  // Check if the excluded combination for a pair is new
  isPairExcludedCombinationNew(pair: { recipient: AnonymizedParticipant | Participant | null; donor: AnonymizedParticipant | Participant | null; alternative: boolean }): boolean {
    const excludedCombination = this.findExcludedCombinationForPair(pair);
    return excludedCombination ? this.isCrossmatchNew(excludedCombination) : false;
  }

  // Check if user has HLA_LAB or OPERATOR role (can add/delete crossmatches)
  isHlaRole(): boolean {
    const role = this.currentUser?.activeRole?.enumName;
    return role === 'HLA_LAB' || role === 'OPERATOR';
  }

  // Check if user has ALLOCATION role (can only add/delete excluded combinations)
  isAllocationRole(): boolean {
    const role = this.currentUser?.activeRole?.enumName;
    return role === 'ALLOCATION';
  }

  // Get tooltip text for add button based on role
  getAddTooltipText(): string {
    if (this.isAllocationRole()) {
      return 'Add excluded combination for this pair';
    }
    return 'Add crossmatch for this pair';
  }

  // Get tooltip text for actions button based on role
  getActionsTooltipText(): string {
    if (this.isAllocationRole()) {
      return 'Match actions';
    }
    return 'Crossmatch actions';
  }

  // Check if user can delete a specific crossmatch based on role
  canDeleteCrossmatch(pair: { recipient: AnonymizedParticipant | Participant | null; donor: AnonymizedParticipant | Participant | null; alternative: boolean }): boolean {
    const crossmatch = this.findCrossmatchResultForPair(pair);
    if (!crossmatch) {
      return false;
    }

    const role = this.currentUser?.activeRole?.enumName;

    // Operator role can delete any crossmatch
    if (role === 'OPERATOR') {
      return true;
    }

    // HLA role can delete crossmatch results
    if (this.isHlaRole()) {
      return true;
    }

    return false;
  }

  // Get delete button label based on what type of crossmatch it is
  getDeleteButtonLabel(pair: { recipient: AnonymizedParticipant | Participant | null; donor: AnonymizedParticipant | Participant | null; alternative: boolean }): string {
    const excludedCombination = this.findExcludedCombinationForPair(pair);
    if (excludedCombination) {
      return 'Delete Excluded Combination';
    }
    return 'Delete Crossmatch';
  }

  // Check if pair has a crossmatch result (positive or negative)
  // Returns true if the 'positive' field is defined (true or false), regardless of excluded status
  hasCrossmatchResult(pair: { recipient: AnonymizedParticipant | Participant | null; donor: AnonymizedParticipant | Participant | null; alternative: boolean }): boolean {
    return this.findCrossmatchResultForPair(pair) !== null;
  }

  // Check if pair has an excluded combination
  hasExcludedCombination(pair: { recipient: AnonymizedParticipant | Participant | null; donor: AnonymizedParticipant | Participant | null; alternative: boolean }): boolean {
    return this.findExcludedCombinationForPair(pair) !== null;
  }

  // Check if user can delete crossmatch result
  // User can delete the crossmatch result portion if the 'positive' field is defined
  canDeleteCrossmatchResult(pair: { recipient: AnonymizedParticipant | Participant | null; donor: AnonymizedParticipant | Participant | null; alternative: boolean }): boolean {
    const crossmatch = this.findCrossmatchResultForPair(pair);
    if (!crossmatch) {
      return false;
    }

    const role = this.currentUser?.activeRole?.enumName;

    // Operator can delete any crossmatch
    if (role === 'OPERATOR') {
      return true;
    }

    // HLA role can delete positive/negative crossmatches
    if (this.isHlaRole()) {
      return true;
    }

    return false;
  }

  // Check if user can delete excluded combination
  canDeleteExcludedCombination(pair: { recipient: AnonymizedParticipant | Participant | null; donor: AnonymizedParticipant | Participant | null; alternative: boolean }): boolean {
    const crossmatch = this.findExcludedCombinationForPair(pair);
    if (!crossmatch) {
      return false;
    }

    const role = this.currentUser?.activeRole?.enumName;

    // Operator can delete any crossmatch (including excluded combinations)
    if (role === 'OPERATOR') {
      return true;
    }

    // Allocation role can delete excluded combinations
    if (this.isAllocationRole()) {
      return true;
    }

    return false;
  }

  // Delete excluded combination
  deleteExcludedCombination(pair: { recipient: AnonymizedParticipant | null; donor: AnonymizedParticipant | null; alternative: boolean }): void {
    if (!pair.recipient || !pair.donor) {
      this.notifyService.withAutodismiss('Both recipient and donor must be available to delete excluded combination');
      return;
    }

    const crossmatch = this.findExcludedCombinationForPair(pair);
    if (!crossmatch) {
      this.notifyService.withAutodismiss('No excluded combination found for this pair');
      return;
    }

    if (confirm('Are you sure you want to delete this excluded combination?')) {
      // Simply delete the excluded combination entity
      // (We're now tracking crossmatch results and excluded combinations separately)
      this.crossmatchService.deleteCrossmatch(crossmatch.id).subscribe({
        next: () => {
          this.loadCrossmatches();
          this.notifyService.withAutodismiss('Excluded combination deleted successfully');
        },
        error: (error) => {
          console.error('Error deleting excluded combination:', error);
          this.notifyService.withAutodismiss('Failed to delete excluded combination. Please try again.');
        }
      });
    }
  }

  // Determine if add button should be shown
  canShowAddButton(pair: { recipient: AnonymizedParticipant | Participant | null; donor: AnonymizedParticipant | Participant | null; alternative: boolean }): boolean {
    // Can show add button if:
    // 1. No crossmatch result exists (can add crossmatch)
    // 2. No excluded combination exists (can add excluded combination)
    return !this.hasCrossmatchResult(pair) || !this.hasExcludedCombination(pair);
  }

  // Determine if delete button should be shown
  canShowDeleteButton(pair: { recipient: AnonymizedParticipant | Participant | null; donor: AnonymizedParticipant | Participant | null; alternative: boolean }): boolean {
    // Show delete button if user can delete either crossmatch result or excluded combination
    const canDeleteCrossmatch = this.hasCrossmatchResult(pair) && this.canDeleteCrossmatchResult(pair);
    const canDeleteExcluded = this.hasExcludedCombination(pair) && this.canDeleteExcludedCombination(pair);
    return canDeleteCrossmatch || canDeleteExcluded;
  }

  // Check if all crossmatches in the chain are negative
  allCrossmatchesNegative(): boolean {
    // Get all pairs that should have crossmatches (pairs with both recipient and donor, excluding alternatives)
    const pairsRequiringCrossmatches = this._groupedData.filter(pair =>
      pair.recipient !== null && pair.donor !== null && !pair.alternative
    );

    // If no pairs exist, return false
    if (pairsRequiringCrossmatches.length === 0) {
      return false;
    }

    // Check if EVERY pair has a crossmatch AND all crossmatches are negative
    return pairsRequiringCrossmatches.every(pair => {
      const crossmatch = this.findCrossmatchResultForPair(pair);
      // Must have a crossmatch, must be negative (positive === false), and must not be excluded
      const excludedCombination = this.findExcludedCombinationForPair(pair);
      return crossmatch !== null && crossmatch.positive === false && !excludedCombination;
    });
  }

  // Check if chain is selected for transplantation
  isSelectedForTransplantation(): boolean {
    return this.chainStatus?.status === 'selected_for_transplantation';
  }

  // Check if chain is unselected for transplantation
  isUnselectedForTransplantation(): boolean {
    return this.chainStatus?.status === 'unselected_for_transplantation';
  }

  // Check if chain can be selected for transplantation
  canSelectForTransplantation(): boolean {
    // Cannot select if already selected or transplanted
    if (this.isSelectedForTransplantation() || this.chainStatus?.status === 'transplanted') {
      return false;
    }

    // Can only select if all crossmatches are negative
    return this.allCrossmatchesNegative();
  }

  // Get tooltip for select for transplantation button
  getSelectForTransplantationTooltip(): string {
    if (this.isSelectedForTransplantation()) {
      return 'Chain is already selected for transplantation';
    }
    if (this.chainStatus?.status === 'transplanted') {
      return 'Chain is already transplanted';
    }
    if (!this.allCrossmatchesNegative()) {
      return 'Negative crossmatches required';
    }
    return 'Select this chain for transplantation';
  }

  // Open select for transplantation dialog
  selectForTransplantation(): void {
    const dialogRef = this.dialog.open(SelectTransplantDialogComponent, {
      width: '600px',
      data: {
        chainNumber: this.chainNumber,
        matchRunNumber: this.matchRunNumber
      }
    });

    dialogRef.afterClosed().subscribe((result: SelectTransplantDialogResult) => {
      if (result?.confirmed) {
        this.chainStatusService.selectForTransplantation(this.matchRunId, parseInt(this.chainNumber)).subscribe({
          next: (status) => {
            this.chainStatus = status;
            this.checkIfChainStatusNew();
            this.notifyService.withAutodismiss('Chain selected for transplantation successfully');
          },
          error: (error) => {
            console.error('Error selecting chain for transplantation:', error);
            this.notifyService.withAutodismiss('Failed to select chain for transplantation. Please try again.');
          }
        });
      }
    });
  }

  // Open unselect transplant dialog
  unselectForTransplantation(): void {
    const dialogRef = this.dialog.open(UnselectTransplantDialogComponent, {
      width: '600px',
      data: {
        chainNumber: this.chainNumber,
        matchRunNumber: this.matchRunNumber
      }
    });

    dialogRef.afterClosed().subscribe((result: UnselectTransplantDialogResult) => {
      if (result?.confirmed && result.reason) {
        this.chainStatusService.unselectForTransplantation(this.matchRunId, parseInt(this.chainNumber), result.reason).subscribe({
          next: (response) => {
            // Backend now automatically deletes the chain status when unselecting
            // Check if the response indicates deletion (backend returns {deleted: true})
            if (response && 'deleted' in response && (response as { deleted: boolean }).deleted) {
              this.chainStatus = null;
              this.isChainStatusNew = false;
              this.notifyService.withAutodismiss('Chain unselected for transplantation successfully');
            } else {
              // Fallback: if backend didn't delete, try to delete manually (for backwards compatibility)
              this.chainStatusService.deleteChainStatus(this.matchRunId, parseInt(this.chainNumber)).subscribe({
                next: () => {
                  this.chainStatus = null;
                  this.isChainStatusNew = false;
                  this.notifyService.withAutodismiss('Chain unselected for transplantation and unlocked successfully');
                },
                error: (unlockError) => {
                  console.error('Error unlocking chain after unselection:', unlockError);
                  // Still show success for unselection even if unlock fails
                  this.chainStatus = response as ChainStatus;
                  this.checkIfChainStatusNew();
                  this.notifyService.withAutodismiss('Chain unselected but failed to unlock. Please unlock manually.');
                }
              });
            }
          },
          error: (error) => {
            console.error('Error unselecting chain for transplantation:', error);
            this.notifyService.withAutodismiss('Failed to unselect chain for transplantation. Please try again.');
          }
        });
      }
    });
  }

  // Check if crossmatch/exclusion actions should be disabled
  areCrossmatchActionsDisabled(): boolean {
    return this.isSelectedForTransplantation();
  }

  // Toggle chain comment expanded state
  toggleChainCommentExpanded(): void {
    this.isChainCommentExpanded = !this.isChainCommentExpanded;
  }

  // Open dialog to add/edit comment for non-locked chains
  openCommentDialog(): void {
    const dialogRef = this.dialog.open(ChainCommentDialogComponent, {
      width: '500px',
      data: {
        chainNumber: this.chainNumber,
        currentComment: this.chainStatus?.comment || ''
      }
    });

    dialogRef.afterClosed().subscribe((result: ChainCommentDialogResult | undefined) => {
      if (result) {
        this.saveChainComment(result.comment);
      }
    });
  }

  // Save comment to chain (creates or updates ChainStatus)
  saveChainComment(comment: string): void {
    this.chainStatusService.setChainComment(this.matchRunId, parseInt(this.chainNumber), comment).subscribe({
      next: (updatedStatus: ChainStatus) => {
        this.chainStatus = updatedStatus;
        this.notifyService.withAutodismiss('Comment saved successfully');
      },
      error: (error) => {
        console.error('Error saving chain comment:', error);
        this.notifyService.withAutodismiss('Failed to save comment');
      }
    });
  }

  // Get unique key for a pair
  private getPairKey(pair: { recipient: AnonymizedParticipant | Participant | null; donor: AnonymizedParticipant | Participant | null; alternative: boolean }): string {
    return `${pair.recipient?.enisNumber || 'null'}-${pair.donor?.enisNumber || 'null'}`;
  }

  // Check if a pair has been confirmed for transplantation
  isPairConfirmed(pair: { recipient: AnonymizedParticipant | Participant | null; donor: AnonymizedParticipant | Participant | null; alternative: boolean }): boolean {
    const pairKey = this.getPairKey(pair);

    // Check if confirmed in current session
    if (this.confirmedPairs.has(pairKey)) {
      return true;
    }

    // Check if both participants already have transplanted status from backend
    if (pair.recipient?.transplanted && pair.donor?.transplanted) {
      return true;
    }

    return false;
  }

  // Check if all pairs in the chain are confirmed for transplantation
  areAllPairsConfirmed(): boolean {
    // Get all pairs that should be confirmed (excluding alternatives and pairs without both participants)
    const pairsRequiringConfirmation = this._groupedData.filter(pair =>
      pair.recipient !== null && pair.donor !== null && !pair.alternative
    );

    // If no pairs exist, return false
    if (pairsRequiringConfirmation.length === 0) {
      return false;
    }

    // Check if ALL pairs are confirmed
    return pairsRequiringConfirmation.every(pair => this.isPairConfirmed(pair));
  }

  // Confirm a pair for transplantation
  confirmPairTransplantation(pair: { recipient: AnonymizedParticipant | null; donor: AnonymizedParticipant | null; alternative: boolean }): void {
    if (!pair.recipient || !pair.donor) {
      return;
    }

    // Check if participants are anonymized (fully or partially)
    if (pair.recipient.enisNumber === '******' || pair.donor.enisNumber === '******' ||
        pair.recipient['abo'] === '******' || pair.donor['abo'] === '******') {
      this.notifyService.withAutodismiss('You do not have permission to confirm transplantation for anonymized participants');
      return;
    }

    // OPTIMIZATION: Look up actual participants from participantMap (O(1) instead of O(N))
    const recipient = this.participantMap.get(pair.recipient!.enisNumber) as Recipient;
    const donor = this.participantMap.get(pair.donor!.enisNumber) as Donor;

    if (!recipient || !donor) {
      this.notifyService.withAutodismiss('Could not find participant data');
      return;
    }

    // Show confirmation dialog
    const dialogRef = this.dialog.open(ConfirmTransplantationDialogComponent, {
      data: {
        recipientName: `${pair.recipient.firstName} ${pair.recipient.lastName}`,
        donorName: `${pair.donor.firstName} ${pair.donor.lastName}`,
        recipientEnis: pair.recipient.enisNumber,
        donorEnis: pair.donor.enisNumber
      },
      width: '600px'
    });

    dialogRef.afterClosed().subscribe((result: ConfirmTransplantationDialogResult) => {
      if (!result || !result.confirmed) {
        return; // User cancelled
      }

      const pairKey = this.getPairKey(pair);

      // Update both recipient and donor with transplanted status (but NOT historical yet)
      const recipientUpdate = {
        ...recipient,
        transplanted: true,
        reportedTransplantDate: new Date().toISOString().split('T')[0]
      };

      const donorUpdate = {
        ...donor,
        transplanted: true,
        reportedTransplantDate: new Date().toISOString().split('T')[0]
      };

      // Call both update services in parallel
      forkJoin({
        recipient: this.participantService.updateRecipient(recipientUpdate),
        donor: this.participantService.updateDonor(donorUpdate)
      }).subscribe({
        next: (results) => {
          this.confirmedPairs.add(pairKey);
          this.notifyService.withAutodismiss(`Transplantation confirmed for ${pair.recipient!.firstName} ${pair.recipient!.lastName} ← ${pair.donor!.firstName} ${pair.donor!.lastName}`);

          // Update local participants array - preserve the original linked data
          const recipientIndex = this.allParticipants.findIndex(p => p.enisNumber === pair.recipient!.enisNumber);
          if (recipientIndex !== -1) {
            // Preserve the original object but update transplanted status
            this.allParticipants[recipientIndex] = {
              ...this.allParticipants[recipientIndex],
              transplanted: true,
              reportedTransplantDate: results.recipient.reportedTransplantDate
            };
            this.participantMap.set(pair.recipient!.enisNumber, this.allParticipants[recipientIndex]);
          }

          const donorIndex = this.allParticipants.findIndex(p => p.enisNumber === pair.donor!.enisNumber);
          if (donorIndex !== -1) {
            // Preserve the original object but update transplanted status
            this.allParticipants[donorIndex] = {
              ...this.allParticipants[donorIndex],
              transplanted: true,
              reportedTransplantDate: results.donor.reportedTransplantDate
            };
            this.participantMap.set(pair.donor!.enisNumber, this.allParticipants[donorIndex]);
          }

          // Check if participants should be set to historical by looking up their Pairs
          const updatedRecipient = this.participantMap.get(pair.recipient!.enisNumber) as Recipient;
          const updatedDonor = this.participantMap.get(pair.donor!.enisNumber) as Donor;

          // Fetch all pairs to check if these participants belong to any pairs
          this.pairService.getAllPairs().subscribe({
            next: (pairs: Pair[]) => {
              this.checkAndSetParticipantHistoricalUsingPairs(updatedRecipient, pairs);
              this.checkAndSetParticipantHistoricalUsingPairs(updatedDonor, pairs);
            },
            error: (err: Error) => {
              console.error('Error fetching pairs for historical check:', err);
            }
          });
        },
        error: (error) => {
          console.error('Error confirming transplantation:', error);
          this.notifyService.withAutodismiss('Failed to confirm transplantation. Please try again.');
        }
      });
    });
  }

  /**
   * Check if a participant should be set to historical using Pair data
   * Called after confirming a chain link transplantation
   * Logic:
   * - If participant belongs to a pair and the pair partner is also transplanted, set both + pair to historical
   * - If participant doesn't belong to a pair (solo), set them to historical immediately
   */
  private checkAndSetParticipantHistoricalUsingPairs(participant: Participant, allPairs: Pair[]): void {
    // Find if this participant belongs to a pair (check both as donor and as recipient)
    let pairAsDonor: Pair | undefined = allPairs.find((p: Pair) => p.donor.id === participant.id);
    let pairAsRecipient: Pair | undefined = allPairs.find((p: Pair) => p.recipient.id === participant.id);

    if (pairAsDonor) {
      // This participant is a donor in a pair
      const donor = participant as Donor;
      const pairedRecipient = this.participantMap.get(pairAsDonor.recipient.enisNumber) as Recipient;

      if (pairedRecipient && donor.transplanted && pairedRecipient.transplanted) {
        // Both are transplanted - set original pair to historical
        this.setParticipantsAndPairHistorical(donor, pairedRecipient);
      }
    } else if (pairAsRecipient) {
      // This participant is a recipient in a pair
      const recipient = participant as Recipient;
      const pairedDonor = this.participantMap.get(pairAsRecipient.donor.enisNumber) as Donor;

      if (pairedDonor && recipient.transplanted && pairedDonor.transplanted) {
        // Both are transplanted - set original pair to historical
        this.setParticipantsAndPairHistorical(pairedDonor, recipient);
      }
    } else {
      // Participant has no pair - solo participant
      if ((participant as any).transplanted) {
        this.setSoloParticipantHistorical(participant);
      }
    }
  }

  /**
   * Set a solo participant (no pair) to historical
   */
  private setSoloParticipantHistorical(participant: Participant): void {
    const now = new Date().toISOString();
    const historicalReason = 'Transplanted within cross-over program';

    const updatedParticipant = {
      ...participant,
      historical: true,
      historicalDate: now,
      historicalReason: historicalReason
    };

    // Update the participant based on type
    if (participant.type === 'Donor') {
      this.participantService.updateDonor(updatedParticipant as Donor).subscribe({
        next: (result: Donor) => {
          // Update local arrays
          const index = this.allParticipants.findIndex((p: Participant) => p.enisNumber === participant.enisNumber);
          if (index !== -1) {
            this.allParticipants[index] = { ...this.allParticipants[index], historical: true };
            this.participantMap.set(participant.enisNumber, this.allParticipants[index]);
          }
          console.log('Solo donor set to historical:', result.enisNumber);
        },
        error: (err: Error) => {
          console.error('Error setting solo donor to historical:', err);
        }
      });
    } else if (participant.type === 'Recipient') {
      this.participantService.updateRecipient(updatedParticipant as Recipient).subscribe({
        next: (result: Recipient) => {
          // Update local arrays
          const index = this.allParticipants.findIndex((p: Participant) => p.enisNumber === participant.enisNumber);
          if (index !== -1) {
            this.allParticipants[index] = { ...this.allParticipants[index], historical: true };
            this.participantMap.set(participant.enisNumber, this.allParticipants[index]);
          }
          console.log('Solo recipient set to historical:', result.enisNumber);
        },
        error: (err: Error) => {
          console.error('Error setting solo recipient to historical:', err);
        }
      });
    }
  }

  /**
   * Set both participants in an original pair and their Pair entity to historical
   */
  private setParticipantsAndPairHistorical(donor: Donor, recipient: Recipient): void {
    const now = new Date().toISOString();
    const historicalReason = 'Transplanted within cross-over program';

    // Update donor to historical
    const updatedDonor = {
      ...donor,
      historical: true,
      historicalDate: now,
      historicalReason: historicalReason
    };

    // Update recipient to historical
    const updatedRecipient = {
      ...recipient,
      historical: true,
      historicalDate: now,
      historicalReason: historicalReason
    };

    // Update both participants
    forkJoin({
      donor: this.participantService.updateDonor(updatedDonor),
      recipient: this.participantService.updateRecipient(updatedRecipient)
    }).subscribe({
      next: (results: { donor: Donor; recipient: Recipient }) => {
        // Update local arrays
        const donorIndex = this.allParticipants.findIndex((p: Participant) => p.enisNumber === donor.enisNumber);
        if (donorIndex !== -1) {
          this.allParticipants[donorIndex] = { ...this.allParticipants[donorIndex], historical: true };
          this.participantMap.set(donor.enisNumber, this.allParticipants[donorIndex]);
        }

        const recipientIndex = this.allParticipants.findIndex((p: Participant) => p.enisNumber === recipient.enisNumber);
        if (recipientIndex !== -1) {
          this.allParticipants[recipientIndex] = { ...this.allParticipants[recipientIndex], historical: true };
          this.participantMap.set(recipient.enisNumber, this.allParticipants[recipientIndex]);
        }

        // Find and update the original Pair entity to historical
        // We need to find the pair by checking donor/recipient IDs
        this.findAndUpdatePairHistorical(results.donor, results.recipient, now, historicalReason);
      },
      error: (err: Error) => {
        console.error('Error setting original pair to historical:', err);
      }
    });
  }

  /**
   * Find the Pair entity for a donor-recipient pair and set it to historical
   */
  private findAndUpdatePairHistorical(donor: Donor, recipient: Recipient, historicalDate: string, historicalReason: string): void {
    // We need to fetch pairs to find the matching one
    // This could be optimized by passing pairs data from parent if needed
    this.pairService.getAllPairs().subscribe({
      next: (pairs: Pair[]) => {
        const matchingPair = pairs.find((p: Pair) =>
          p.donor.id === donor.id && p.recipient.id === recipient.id
        );

        if (matchingPair) {
          console.log('Found matching pair:', matchingPair.id);
          const updatedPair = {
            ...matchingPair,
            historical: true,
            historicalDate: historicalDate,
            historicalReason: historicalReason
          };

          this.pairService.updatePair(updatedPair).subscribe({
            next: (result: Pair) => {
              console.log('Pair successfully set to historical:', result.id);
              this.notifyService.withAutodismiss(`Original pair (${donor.enisNumber} - ${recipient.enisNumber}) set to historical`);
            },
            error: (err: Error) => {
              console.error('Error setting pair to historical:', err);
            }
          });
        } else {
          console.warn('No matching pair found for Donor:', donor.id, 'Recipient:', recipient.id);
        }
      },
      error: (err: Error) => {
        console.error('Error fetching pairs:', err);
      }
    });
  }

  // Format Unix timestamp to human-readable date
  getFormattedDate(timestamp: number): string {
    if (!timestamp) return 'Unknown';

    // Convert from seconds to milliseconds if needed
    // Timestamps > 1000000000000 are already in milliseconds
    const dateInMs = timestamp > 1000000000000 ? timestamp : timestamp * 1000;
    const date = new Date(dateInMs);

    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // ========== PARTIAL LOCK / MERGED CHAIN METHODS ==========

  isMergedChain(): boolean {
    return this.chainStatus?.lockType === 'merged';
  }

  isPartiallyLockedChain(): boolean {
    return this.chainStatus?.lockType === 'partial';
  }

  isAltruisticChainWithWaitlist(): boolean {
    const sortedData = [...this.data].sort((a, b) => (a.orderIndex || 1) - (b.orderIndex || 1));
    const firstDonor = sortedData.find(item => item.type === 'D');

    if (!firstDonor) return false;

    const donor = this.findParticipant(firstDonor.enisNumber);

    // Any chain starting with an altruist always ends with a waitlist patient
    // (PM waitlist, priority, or privilege) so only the altruist check is needed
    return !!(donor && (donor as any).altruist === true);
  }

  getMergedChainStartIndex(): number {
    return this.chainStatus?.mergedChainStartOrderIndex || 0;
  }

  isParticipantInLockedPortion(participant: Participant | AnonymizedParticipant | null): boolean {
    if (!participant) return false;

    const matchData = this.data.find(item => item.enisNumber === participant.enisNumber);
    if (!matchData) return false;

    // For merged chains: check if participant is before the merge point
    if (this.isMergedChain()) {
      return (matchData.orderIndex || 0) < this.getMergedChainStartIndex();
    }

    // For partial lock chains: check if participant is in the fully locked portion
    // Fully locked portion = all participants before the partially locked donor
    if (this.isPartiallyLockedChain() && this.chainStatus?.partiallyLockedDonorEnis) {
      // Find the order index of the partially locked donor
      const partialDonorData = this.data.find(item => item.enisNumber === this.chainStatus?.partiallyLockedDonorEnis);
      if (partialDonorData) {
        // Participants before the partial donor are fully locked
        return (matchData.orderIndex || 0) < (partialDonorData.orderIndex || 0);
      }
    }

    return false;
  }

  isParticipantInNewPortion(participant: Participant | AnonymizedParticipant | null): boolean {
    if (!participant || !this.isMergedChain()) return false;

    const matchData = this.data.find(item => item.enisNumber === participant.enisNumber);
    if (!matchData) return false;

    return (matchData.orderIndex || 0) >= this.getMergedChainStartIndex();
  }

  isPartiallyLockedDonor(participant: Participant | AnonymizedParticipant | null): boolean {
    if (!participant) return false;

    if (this.chainStatus?.lockType === 'partial') {
      return participant.enisNumber === this.chainStatus.partiallyLockedDonorEnis;
    }

    if (this.chainStatus?.lockType === 'merged') {
      const matchData = this.data.find(item => item.enisNumber === participant.enisNumber);
      return matchData?.orderIndex === this.getMergedChainStartIndex();
    }

    return false;
  }

  isPairedRecipient(participant: Participant | AnonymizedParticipant | null): boolean {
    if (!participant) return false;

    // Check if this is a recipient receiving from the partially locked donor
    // These are the waitlist recipients (and any other recipients after the donor)
    if ((this.chainStatus?.lockType === 'partial' || this.chainStatus?.lockType === 'merged') && this.chainStatus.waitlistRecipientsEnis) {
      return this.chainStatus.waitlistRecipientsEnis.includes(participant.enisNumber);
    }

    return false;
  }

  isWaitlistPatient(participant: Participant | AnonymizedParticipant | null): boolean {
    if (!participant) return false;
    return participant.type === 'recipient' && (participant as any).postmortumwaitlist === true;
  }

  // Check if a donor is an altruist (first donor with no matched recipient in the first pair)
  isAltruistDonor(donor: AnonymizedParticipant | Participant | null): boolean {
    if (!donor || !this._groupedData.length) return false;

    // Check if donor is an altruistic donor
    return !!(donor as Donor).altruist;
  }

  // Check if a recipient is the first waitlist recipient (recipient in first pair after altruist)
  isFirstWaitlistRecipient(recipient: AnonymizedParticipant | Participant | null): boolean {
    if (!recipient || !this._groupedData.length) return false;

    // Check if recipient is on postmortem waitlist
    return !!(recipient as Recipient).postmortumwaitlist;
  }

  // Check if this pair should have a separator line above it (first alternative)
  shouldShowAlternativeSeparator(index: number): boolean {
    if (index === 0) return false;

    // Show separator if this is the first alternative pair
    const currentPair = this.groupedData[index];
    const previousPair = this.groupedData[index - 1];

    return currentPair.alternative && !previousPair.alternative;
  }

  // Check if this pair should have a separator line above it (transition from locked to new portion in merged chain)
  shouldShowMergedChainSeparator(index: number): boolean {
    if (index === 0 || !this.isMergedChain()) return false;

    const currentPair = this.groupedData[index];
    const previousPair = this.groupedData[index - 1];

    // Show separator when transitioning from locked portion to new portion
    // Previous pair has locked portion, current pair has new portion
    const prevHasLockedRecipient = !!(previousPair.recipient && this.isParticipantInLockedPortion(previousPair.recipient));
    const prevHasLockedDonor = !!(previousPair.donor && this.isParticipantInLockedPortion(previousPair.donor));
    const currHasNewRecipient = !!(currentPair.recipient && this.isParticipantInNewPortion(currentPair.recipient));
    const currHasNewDonor = !!(currentPair.donor && this.isParticipantInNewPortion(currentPair.donor));

    return (prevHasLockedRecipient || prevHasLockedDonor) && (currHasNewRecipient || currHasNewDonor);
  }

  isParticipantInFullyLockedChain(participant: Participant | AnonymizedParticipant | null): boolean {
    if (!participant) return false;

    // Check if this chain is fully locked (lockType is 'full' or undefined/null, and status exists)
    // IMPORTANT: Exclude 'selected_for_transplantation' status - those chains should show green assignment icons, not blue locks
    // IMPORTANT: Exclude chains with null status (comment-only) - those are not locked
    const isFullyLocked = !!(this.chainStatus &&
                             this.chainStatus.status != null &&
                             this.chainStatus.status !== 'selected_for_transplantation' &&
                             (this.chainStatus.lockType === 'full' || !this.chainStatus.lockType) &&
                             !this.isMergedChain() &&
                             !this.isPartiallyLockedChain());

    return isFullyLocked;
  }

  lockChainUpToWaitlist(): void {
    const sortedData = [...this.data].sort((a, b) => (a.orderIndex || 1) - (b.orderIndex || 1));
    const recipients = sortedData.filter(item => item.type === 'P');
    const waitlistRecipient = recipients[recipients.length - 1];

    const dialogRef = this.dialog.open(ChainLockDialogComponent, {
      width: '600px',
      data: {
        chainNumber: this.chainNumber,
        matchRunNumber: this.matchRunNumber,
        action: 'lock_up_to_waitlist',
        waitlistEnisNumber: waitlistRecipient?.enisNumber
      }
    });

    dialogRef.afterClosed().subscribe((result: ChainLockDialogResult) => {
      if (result?.confirmed) {
        this.chainStatusService.lockChainPartially(
          this.matchRunId,
          parseInt(this.chainNumber),
          result.comment || ''
        ).subscribe({
          next: () => {
            this.notifyService.withAutodismiss('Chain locked up to waitlist successfully');
            this.loadChainStatus();
            this.updateGroupedData();
          },
          error: (error) => {
            this.notifyService.withAutodismiss('Failed to lock chain: ' + error.message);
          }
        });
      }
    });
  }

  lockEntireMergedChain(): void {
    const dialogRef = this.dialog.open(ChainLockDialogComponent, {
      width: '600px',
      data: {
        chainNumber: this.chainNumber,
        matchRunNumber: this.matchRunNumber,
        action: 'lock_merged_chain'
      }
    });

    dialogRef.afterClosed().subscribe((result: ChainLockDialogResult) => {
      if (result?.confirmed) {
        this.chainStatusService.lockMergedChainFully(
          this.matchRunId,
          parseInt(this.chainNumber),
          result.comment || ''
        ).subscribe({
          next: () => {
            this.notifyService.withAutodismiss('Entire merged chain locked successfully');
            this.loadChainStatus();
            this.updateGroupedData();
          },
          error: (error) => {
            this.notifyService.withAutodismiss('Failed to lock chain: ' + error.message);
          }
        });
      }
    });
  }

  unlockEntireChain(): void {
    const dialogRef = this.dialog.open(ChainLockDialogComponent, {
      width: '600px',
      data: {
        chainNumber: this.chainNumber,
        matchRunNumber: this.matchRunNumber,
        action: 'unlock_entire_chain'
      }
    });

    dialogRef.afterClosed().subscribe((result: ChainLockDialogResult) => {
      if (result?.confirmed) {
        this.chainStatusService.unlockEntireChain(
          this.matchRunId,
          parseInt(this.chainNumber)
        ).subscribe({
          next: () => {
            this.notifyService.withAutodismiss('Entire chain unlocked successfully');
            this.loadChainStatus();
            this.updateGroupedData();
          },
          error: (error) => {
            this.notifyService.withAutodismiss('Failed to unlock chain: ' + error.message);
          }
        });
      }
    });
  }

  // Get tooltip text for crossmatch result chip
  getCrossmatchResultTooltip(pair: { recipient: AnonymizedParticipant | Participant | null; donor: AnonymizedParticipant | Participant | null; alternative: boolean }): string {
    const crossmatch = this.findCrossmatchResultForPair(pair);
    if (!crossmatch) {
      return '';
    }

    const tooltipLines: string[] = [];

    // Add result
    if (crossmatch.positive === true) {
      tooltipLines.push('Result: Positive');
    } else if (crossmatch.positive === false) {
      tooltipLines.push('Result: Negative');
    } else {
      tooltipLines.push('Result: Pending');
    }

    // Add sample date
    if (crossmatch.sampleDate) {
      const sampleDate = new Date(crossmatch.sampleDate);
      tooltipLines.push(`Sample date: ${sampleDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`);
    }

    // Add result date
    if (crossmatch.resultDate) {
      const resultDate = new Date(crossmatch.resultDate);
      tooltipLines.push(`Result date: ${resultDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`);
    }

    // Add comment
    if (crossmatch.comments) {
      tooltipLines.push(`Comment: ${crossmatch.comments}`);
    }

    return tooltipLines.join('\n');
  }

  // Get tooltip text for excluded combination chip
  getExcludedCombinationTooltip(pair: { recipient: AnonymizedParticipant | Participant | null; donor: AnonymizedParticipant | Participant | null; alternative: boolean }): string {
    const excludedCombination = this.findExcludedCombinationForPair(pair);
    if (!excludedCombination) {
      return '';
    }

    const tooltipLines: string[] = [];

    tooltipLines.push('Status: Excluded');

    // Add reason if available
    if (excludedCombination.reasonExcluded) {
      tooltipLines.push(`Reason: ${excludedCombination.reasonExcluded}`);
    }

    // Add date created
    if (excludedCombination.dateCreated) {
      const dateCreated = new Date(excludedCombination.dateCreated);
      tooltipLines.push(`Date created: ${dateCreated.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`);
    }

    // Add comment
    if (excludedCombination.comments) {
      tooltipLines.push(`Comment: ${excludedCombination.comments}`);
    }

    return tooltipLines.join('\n');
  }

  // Get transplant type for a pair with anonymization logic
  // Returns the transplant type only if participants are not anonymized
  // Note: "Regular" transplant types are NOT returned (we only show xABO, xHLA, and xABO xHLA)
  getTransplantTypeForPair(pair: { recipient: AnonymizedParticipant | Participant | null; donor: AnonymizedParticipant | Participant | null; alternative: boolean }): string | null {
    if (!pair.recipient || !pair.donor) {
      return null;
    }

    // Check if participants are anonymized (fully or partially)
    // If they are anonymized, don't show the transplant type
    if (pair.recipient.enisNumber === '******' || pair.donor.enisNumber === '******' ||
        pair.recipient['abo'] === '******' || pair.donor['abo'] === '******') {
      return null;
    }

    // Find the MatchData for the recipient (which contains the transplantType)
    const recipientMatchData = this.data.find(item => item.enisNumber === pair.recipient!.enisNumber);
    if (!recipientMatchData || !recipientMatchData.transplantType) {
      return null;
    }

    // Don't show "Regular" transplant types - only show xABO, xHLA, and xABO xHLA
    const transplantType = recipientMatchData.transplantType;
    if (transplantType.toLowerCase().includes('regular') &&
        !transplantType.toLowerCase().includes('xabo') &&
        !transplantType.toLowerCase().includes('xhla')) {
      return null;
    }

    return recipientMatchData.transplantType;
  }

  // Get label text for transplant type chip
  getTransplantTypeLabel(transplantType: string): string {
    // Map the transplant type to a short label
    if (transplantType.toLowerCase().includes('xabo') && transplantType.toLowerCase().includes('xhla')) {
      return 'ABO-i HLA-i';
    } else if (transplantType.toLowerCase().includes('xabo')) {
      return 'ABO-i';
    } else if (transplantType.toLowerCase().includes('xhla')) {
      return 'HLA-i';
    } else if (transplantType.toLowerCase().includes('regular')) {
      return 'Regular';
    }
    // Fallback to the original value if no match
    return transplantType;
  }

  // Get CSS class for transplant type chip
  getTransplantTypeClass(transplantType: string): string {
    if (transplantType.toLowerCase().includes('xabo') && transplantType.toLowerCase().includes('xhla')) {
      return 'transplant-type-xabo-xhla';
    } else if (transplantType.toLowerCase().includes('xabo')) {
      return 'transplant-type-xabo';
    } else if (transplantType.toLowerCase().includes('xhla')) {
      return 'transplant-type-xhla';
    }
    // Default to xabo styling (amber) if no specific match, though this shouldn't be reached
    return 'transplant-type-xabo';
  }

}
